import React, { useState, useEffect } from 'react';
import { Form, Button, Container, Row, Col, Alert } from 'react-bootstrap';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import supabase from '../supabaseClient';

const PasswordReset = () => {
    const location = useLocation();
    const [email, setEmail] = useState('');
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const [cooldownSeconds, setCooldownSeconds] = useState(0);

    const navigate = useNavigate();
    const { session } = useAuth();

    // Email validation function
    const isValidEmail = (email) => {
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        return emailRegex.test(email);
    };

    // Get email from location state on component mount
    useEffect(() => {
        if (location.state?.email) {
            setEmail(location.state.email);
        }
    }, [location.state]);

    // Handle cooldown timer
    useEffect(() => {
        let interval;
        if (cooldownSeconds > 0) {
            interval = setInterval(() => {
                setCooldownSeconds(prev => {
                    if (prev <= 1) {
                        return 0;
                    }
                    return prev - 1;
                });
            }, 1000);
        }
        return () => {
            if (interval) clearInterval(interval);
        };
    }, [cooldownSeconds]);

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError('');
        setSuccess('');
        
        // Validate email before submitting
        if (!email.trim()) {
            setError('Please enter your email address');
            return;
        }
        
        if (!isValidEmail(email.trim())) {
            setError('Please enter a valid email address');
            return;
        }
        
        setIsLoading(true);
        
        try {
            const { error } = await supabase.auth.resetPasswordForEmail(email.trim(), {
                redirectTo: `${window.location.origin}/password-update`,
            });

            if (error) {
                setError(error.message);
            } else {
                setSuccess('If an account exists with this email, you will receive a password reset link shortly.');
                setCooldownSeconds(60); // Start 60-second cooldown
                // setTimeout(() => {
                //     navigate('/login');
                // }, 5000);
            }
        } catch (error) {
            setError('An error occurred while sending the reset link');
            console.error('Password reset error:', error);
        } finally {
            setIsLoading(false);
        }
    };

    const isButtonDisabled = isLoading || cooldownSeconds > 0;
    const buttonText = isLoading 
        ? 'Sending...' 
        : cooldownSeconds > 0 
            ? `Resend in ${cooldownSeconds}s` 
            : 'Send Reset Link';

    return (
        <Container fluid className="py-5 px-3">
            <Row className="justify-content-center m-0">
                <Col md={6} lg={4}>
                    <div className="text-center mb-4">
                        <h2>Reset Password</h2>
                        <p className="text-muted">Enter your email to receive a password reset link</p>
                    </div>

                    {error && <Alert variant="danger">{error}</Alert>}
                    {success && <Alert variant="success">{success}</Alert>}

                    <Form onSubmit={handleSubmit} className="auth-form">
                        <Form.Group className="mb-4">
                            <Form.Label className="d-block text-start">Email</Form.Label>
                            <Form.Control
                                type="email"
                                value={email}
                                onChange={(e) => setEmail(e.target.value)}
                                required
                                placeholder="Enter your email"
                                className="form-control-custom"
                            />
                        </Form.Group>

                        <Button 
                            variant="primary" 
                            type="submit" 
                            className="auth-primary-btn mb-3"
                            disabled={isButtonDisabled}
                            onClick={handleSubmit}
                        >
                            {buttonText}
                        </Button>

                        <div className="text-center">
                            Remember your password? <a href="/login" className="auth-link">Log in</a>
                        </div>
                    </Form>
                </Col>
            </Row>
        </Container>
    );
};

export default PasswordReset;
